import { useLayoutEffect, useRef, useState } from 'react'
import type {
  KeyboardEvent as ReactKeyboardEvent,
  PointerEvent as ReactPointerEvent,
  ReactNode,
  RefObject,
} from 'react'
import { clampWidthPct, WIDTH_MAX, WIDTH_MIN } from '../../lib/image-geometry'

/**
 * The resize surface shared by the two things a bid author sizes inside a part: an uploaded
 * image (`ImageNodeView.tsx`) and a rendered diagram (`CodeBlockMermaid.tsx`). Both store ONE
 * number — a width in percent of the text column — and both store it in their own markdown
 * slot, so this module owns the gesture and the arithmetic while each node view owns the
 * codec and the commit.
 *
 * FOUR CORNERS, because that is where a hand goes. The aspect ratio is locked, so a corner and
 * an edge compute the same number; an author reaching for the corner of a picture and finding
 * nothing there concludes the feature is missing, which is exactly what happened.
 *
 * ONE COMMIT PER GESTURE. A drag writes `wrapper.style.width` and nothing else until pointerup.
 * Committing per pointermove would turn every mouse movement into a Y.Text write, a server
 * canonicalisation pass and a modification-history entry.
 */

/** The four grab points. */
export type Corner = 'tl' | 'tr' | 'bl' | 'br'

/** Fixed order, so the DOM (and therefore the tab order) is always top row then bottom row. */
export const CORNERS: readonly Corner[] = ['tl', 'tr', 'bl', 'br']

/** Widths a drag snaps to, in percent of the text column — the fractions a bid author asks for. */
const SNAP_TARGETS = [25, 33, 50, 66, 75, 100]
/** How close, in CSS pixels, a drag has to come before it snaps. */
const SNAP_PX = 6
/** What one press of − / + moves the width by. */
const BUTTON_STEP = 5
/** Arrow-key steps on a focused corner: a nudge, and a jump with Shift. */
const KEY_STEP = 1
const KEY_STEP_SHIFT = 10

/**
 * Pull `px` onto a snap target when it lands within SNAP_PX of one, so the everyday widths are
 * reachable with a mouse and come out EXACTLY (25, not 24.9).
 */
function snapToTarget(px: number, columnPx: number): number {
  let best = px
  let bestGap = SNAP_PX
  for (const target of SNAP_TARGETS) {
    const targetPx = (target / 100) * columnPx
    const gap = Math.abs(px - targetPx)
    if (gap <= bestGap) {
      best = targetPx
      bestGap = gap
    }
  }
  return best
}

/** Write the width straight to the DOM. Used by the live drag and to reconcile afterwards. */
export function applyWidth(wrapper: HTMLElement, pct: number | null): void {
  wrapper.style.width = pct == null ? '' : `${pct}%`
  wrapper.classList.toggle('sized', pct != null)
}

/** Accessible names, so an image and a diagram each name the object being resized. */
export interface WidthResizeLabels {
  corner: Record<Corner, string>
  /** The numeric % field. */
  field: string
  /** The − button. */
  dec: string
  /** The + button. */
  inc: string
}

export interface WidthResizeOptions {
  /** The element whose CSS width IS the stored percentage (the node view's wrapper). */
  wrapperRef: RefObject<HTMLElement | null>
  /** The width as the document currently holds it, or null for the natural size. */
  widthPct: number | null
  /** False for a viewer: no corners, no field — an affordance that cannot work is a lie. */
  editable: boolean
  /**
   * Write ONE width into the document — already clamped, null meaning "no explicit width".
   * The caller encodes it into its own markdown slot and reconciles a refused transaction.
   */
  commit: (pct: number | null) => void
  labels: WidthResizeLabels
}

export interface WidthResizeSurface {
  /** The four corner buttons; null for a viewer. Render inside a positioned wrapper. */
  corners: ReactNode
  /** The − / numeric % / + trio; null for a viewer. Render inside the node view's toolbar. */
  tools: ReactNode
}

/**
 * Wire a node view's wrapper for resizing. Returns the two pieces of UI to place — nothing is
 * rendered around them, because the image and the diagram hang their toolbars differently.
 */
export function useWidthResize({
  wrapperRef,
  widthPct,
  editable,
  commit,
  labels,
}: WidthResizeOptions): WidthResizeSurface {
  const draggingRef = useRef(false)
  // What the % field shows while it is being typed into — committing on every keystroke would
  // clamp "4" to "5" under the member's fingers.
  const [draft, setDraft] = useState<string | null>(null)

  // Re-assert the stored width after every render, before paint. React owns the value (it comes
  // from the node attributes) but a drag and a refused commit write the DOM directly, so this is
  // what puts the two back in step — including after React rewrites `className` for a selection
  // change, which would otherwise drop the `sized` class a drag had just added.
  useLayoutEffect(() => {
    const wrapper = wrapperRef.current
    if (wrapper && !draggingRef.current) applyWidth(wrapper, widthPct)
  })

  /** Width of the text column the percentage is measured against; 0 before layout. */
  const columnPx = (): number => wrapperRef.current?.parentElement?.clientWidth ?? 0

  /** The width to nudge from: the stored one, else what the node is actually taking up. */
  const currentPct = (): number => {
    if (widthPct != null) return widthPct
    const column = columnPx()
    const own = wrapperRef.current?.getBoundingClientRect().width ?? 0
    if (column <= 0 || own <= 0) return WIDTH_MAX
    return clampWidthPct((own / column) * 100) ?? WIDTH_MAX
  }

  /** Everything that changes the width funnels through here — one write, one history entry. */
  const change = (pct: number | null): void => {
    setDraft(null)
    commit(pct === null ? null : clampWidthPct(pct))
  }

  const startDrag = (event: ReactPointerEvent<HTMLButtonElement>, corner: Corner): void => {
    const wrapper = wrapperRef.current
    const column = columnPx()
    if (!editable || !wrapper || column <= 0) return
    event.preventDefault()
    event.stopPropagation()

    const handle = event.currentTarget
    const rect = wrapper.getBoundingClientRect()
    const startX = event.clientX
    const startY = event.clientY
    const startPx = rect.width
    // Aspect is locked, so a vertical pull is a horizontal pull in disguise: convert it through
    // the node's own ratio and follow whichever axis the pointer actually travelled furthest on.
    // That is what makes a diagonal corner drag feel like one gesture instead of two.
    const aspect = rect.height > 0 ? rect.width / rect.height : 0
    const dirX = corner === 'tr' || corner === 'br' ? 1 : -1
    const dirY = corner === 'bl' || corner === 'br' ? 1 : -1
    let latest = currentPct()

    handle.setPointerCapture?.(event.pointerId)
    draggingRef.current = true
    wrapper.classList.add('resizing')

    const move = (moveEvent: PointerEvent): void => {
      const dx = dirX * (moveEvent.clientX - startX)
      const dy = dirY * (moveEvent.clientY - startY) * aspect
      const raw = startPx + (Math.abs(dy) > Math.abs(dx) ? dy : dx)
      const bounded = Math.min(column, Math.max((WIDTH_MIN / 100) * column, raw))
      latest = clampWidthPct((snapToTarget(bounded, column) / column) * 100) ?? latest
      // DOM ONLY, deliberately — see the header.
      applyWidth(wrapper, latest)
    }

    const end = (): void => {
      handle.removeEventListener('pointermove', move)
      handle.removeEventListener('pointerup', end)
      handle.removeEventListener('pointercancel', end)
      draggingRef.current = false
      wrapper.classList.remove('resizing')
      change(latest)
    }

    handle.addEventListener('pointermove', move)
    handle.addEventListener('pointerup', end)
    handle.addEventListener('pointercancel', end)
  }

  /** Right always widens and Left always narrows, on every corner — one mental model, not four. */
  const onCornerKey = (event: ReactKeyboardEvent<HTMLButtonElement>): void => {
    // A diagram's corners sit inside a container that opens the source editor on Enter/Space;
    // a key pressed on a handle is for the handle.
    event.stopPropagation()
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
    event.preventDefault()
    const step = event.shiftKey ? KEY_STEP_SHIFT : KEY_STEP
    change(currentPct() + (event.key === 'ArrowRight' ? step : -step))
  }

  /** Empty field = no explicit width, i.e. back to the node's natural size. */
  const commitDraft = (): void => {
    if (draft === null) return
    const trimmed = draft.trim()
    change(trimmed === '' ? null : Number(trimmed))
  }

  if (!editable) return { corners: null, tools: null }

  const corners = (
    <>
      {CORNERS.map((corner) => (
        <button
          key={corner}
          type="button"
          className={`wr-h ${corner}`}
          aria-label={labels.corner[corner]}
          onPointerDown={(e) => startDrag(e, corner)}
          onKeyDown={onCornerKey}
        />
      ))}
    </>
  )

  const tools = (
    <>
      <button
        className="wr-step"
        type="button"
        title={labels.dec}
        aria-label={labels.dec}
        onClick={() => change(currentPct() - BUTTON_STEP)}
      >
        −
      </button>
      <input
        className="wr-w"
        type="number"
        min={WIDTH_MIN}
        max={WIDTH_MAX}
        step={1}
        aria-label={labels.field}
        value={draft ?? (widthPct == null ? '' : String(widthPct))}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commitDraft}
        onKeyDown={(e) => {
          e.stopPropagation()
          if (e.key === 'Enter') {
            e.preventDefault()
            commitDraft()
          }
        }}
      />
      <span className="wr-pct" aria-hidden="true">
        %
      </span>
      <button
        className="wr-step"
        type="button"
        title={labels.inc}
        aria-label={labels.inc}
        onClick={() => change(currentPct() + BUTTON_STEP)}
      >
        +
      </button>
    </>
  )

  return { corners, tools }
}
